<template>
  <div :class="containerClass">
    <page-header
      title="Manage Student Dashboard"
      :container-class="null"
      class="mb-32pt"
      infoText="Manage your student's dashboard."
    />

    <div class="mb-5">
      <draggable class="row draggable-cursor" @start="dragging = true" :list="setupGuideData">
        <div
          class="d-flex align-items-center justify-content-center col-md-6"
          v-for="guide in setupGuideData"
          :key="guide.id"
        >
          <i class="fas fa-bars icon-16pt mr-4"></i>
          <DashboardCard
            :guide="guide"
            :setupStatuses="{}"
            :editable="true"
            style="height: 90%; width: 100%"
            @enable="enable"
          />
        </div>
      </draggable>
      <div class="d-flex">
        <b-btn variant="primary" style="width: 150px" class="btn-normal my-2 ml-auto" @click.prevent="updateOrder">
          Update
        </b-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import DashboardCard from '@/components/DashboardCard.vue';
import Page from '../../../components/Page.vue';
import PageHeader from '../../../components/Ui/PageHeader.vue';
import draggable from 'vuedraggable';

export default {
  extends: Page,
  name: 'OrgManageStudentDashboard',
  components: { PageHeader, DashboardCard, draggable },
  data() {
    return {
      title: 'Home',

      isLoading: false,
      status: null,
      windowWidth: window.innerWidth,
      dragging: false,
      setupGuideData: [],

      // Default cards used when the org has no saved std_dashboard_config yet.
      defaultOrgDashboardConfig: [
        {
          id: 1,
          title: 'Checklist',
          description: 'Track your career readiness.',
          abbr: 'checklist',
          redirectTo: 'student-checklist',
          icon: 'assignment_turned_in',
          color: 'info',
          enable: true,
        },
        {
          id: 2,
          title: 'Community',
          description: 'Connect and grow with others.',
          abbr: 'community',
          redirectTo: 'student-discussions',
          icon: 'people',
          color: 'primary',
          enable: true,
        },
        {
          id: 3,
          title: 'Advisors',
          description: 'Connect with advisors for guidance.',
          abbr: 'advisors',
          redirectTo: 'student-advisors',
          icon: 'supervisor_account',
          color: 'success',
          enable: true,
        },
        {
          id: 4,
          title: 'Explore',
          description: 'Discover resources and more.',
          abbr: 'explore',
          redirectTo: 'student-community',
          icon: 'explore',
          color: 'primary',
          enable: true,
        },
        {
          id: 10,
          title: 'Exam Results',
          description: 'Report your credential exam results.',
          abbr: 'exam_results',
          redirectTo: 'student-exam-results',
          icon: 'score',
          color: 'primary',
          enable: true,
        },
        {
          id: 11,
          title: 'My Licenses',
          description: 'Add your CNA or medication aide license for verification.',
          abbr: 'license_verifications',
          redirectTo: 'student-license-verifications',
          icon: 'card_membership',
          color: 'primary',
          enable: true,
        },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Manage Student Dashboard', active: true },
      ];
    },
    isMobileScreen() {
      return this.windowWidth <= 600;
    },
  },

  methods: {
    ...mapActions('organization', ['updateOrgStudentDashboard', 'getOrgStudentDashboardConfig']),
    get,

    // The logged-in user payload is cached from login, so it can lag behind cards
    // added server-side. Always re-read the org's live config on load.
    async fetchDashboardConfig() {
      try {
        const resp = await this.getOrgStudentDashboardConfig();
        const saved = get(resp, 'data.std_dashboard_config.dashboard_config');
        if (saved && saved.length) {
          this.setupGuideData = JSON.parse(JSON.stringify(saved));
        }
      } catch (err) {
        // Non-fatal: keep whatever the cached user payload already painted.
      }
    },
    enable() {
      // DashboardCard's checkbox uses v-model on guide.enable, so the value is
      // already updated by the time this fires. Nothing else to do here.
    },
    async updateOrder() {
      this.dragging = false;
      const data = {
        std_dashboard_config: { dashboard_config: this.setupGuideData },
      };

      try {
        const resp = await this.updateOrgStudentDashboard(data);
        // The backend mirrors shared cards onto the sidebar tools, so refresh the
        // cached linked_entity to keep the sidebar in step without a reload.
        const sidebarTools = get(resp, 'data.sidebar_tools_config');
        if (sidebarTools) {
          this.$store.commit('auth/updateMyProfile', {
            linked_entity: { ...get(this.getLoggedInUser, 'linked_entity', {}), sidebar_tools_config: sidebarTools },
          });
        }
        this.makeToast({ variant: 'success', msg: 'Student Dashboard Updated' });
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
  watch: {
    getLoggedInUser: {
      immediate: true,
      handler(value) {
        if (value) {
          const saved = get(this.getLoggedInUser, 'linked_entity.std_dashboard_config.dashboard_config');
          // Deep clone so the draggable list / v-model toggles don't mutate Vuex state directly.
          this.setupGuideData = JSON.parse(JSON.stringify(saved && saved.length ? saved : this.defaultOrgDashboardConfig));
        }
      },
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    await this.fetchDashboardConfig();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>
